import { z } from 'zod';
import { Priority, Severity, TicketType } from './enums.js';
/**
 * Assignment agent (smart ticket routing).
 *
 * The agent ranks who should pick up an unassigned ticket from two independent
 * signals: what a developer has PROVEN they work on (resolved-ticket history,
 * recency-weighted) and what they can ACTUALLY absorb right now (sprint capacity
 * hours, work in progress, delivery reliability).
 *
 * Every output here is a SUGGESTION. Nothing in this contract commits an
 * assignment — applying one is a separate, explicit call that records whether the
 * human took the suggestion or overrode it (AC-004, mirroring the AI module).
 */
// ---------------------------------------------------------------------------
// Scoring factors
// ---------------------------------------------------------------------------
/**
 * The seven scored axes. Each contributes `weight` points at a normalized score
 * of 1.0; the candidate's total is the sum. Hard gates (inactive, no capacity,
 * non-delivery role, full PTO) exclude a candidate outright instead of scoring.
 */
export const AssignmentFactorKey = z.enum([
    'affinity', // past work history on this product area / component / repo / type
    'capacity', // sprint capacity hours still free
    'wip', // how many tickets are already in flight
    'throughput', // historically completes what they commit to
    'priorityFit', // track record on this priority / severity band
    'continuity', // already on the epic, a related ticket, or this customer
    'fairness', // spreads new work rather than stacking one person
]);
/** Points each factor contributes at a normalized score of 1.0. Sums to 100. */
export const ASSIGNMENT_FACTOR_WEIGHTS = {
    affinity: 28,
    capacity: 26,
    wip: 16,
    throughput: 10,
    priorityFit: 8,
    continuity: 7,
    fairness: 5,
};
export const ASSIGNMENT_FACTOR_LABELS = {
    affinity: 'Domain affinity',
    capacity: 'Capacity headroom',
    wip: 'Work in progress',
    throughput: 'Delivery reliability',
    priorityFit: 'Priority fit',
    continuity: 'Context continuity',
    fairness: 'Load fairness',
};
/** One scored axis for one candidate, with the evidence behind the number. */
export const AssignmentFactor = z.object({
    key: AssignmentFactorKey,
    /** Normalized 0..1 — how well the candidate does on this axis. */
    score: z.number().min(0).max(1),
    /** Points this factor contributed (score x weight). */
    points: z.number(),
    weight: z.number(),
    /** Human-readable evidence, e.g. "resolved 12 tickets in Dispatch (4 recent)". */
    detail: z.string(),
});
// ---------------------------------------------------------------------------
// Workload
// ---------------------------------------------------------------------------
/** Why a developer was excluded from consideration entirely. */
export const AssignmentGate = z.enum([
    'inactive', // user.active = false
    'non_delivery', // role cannot be assigned delivery work
    'no_capacity', // zero (or negative) free hours in the active sprint
    'full_pto', // day-level overrides zero out the rest of the sprint
    'wip_limit', // already at/over the hard WIP ceiling
]);
/**
 * A developer's live bandwidth. Hours come from SprintCapacity for the active
 * sprint (net of PTO day-overrides); when a developer has no capacity row the
 * sprint defaults are used and `capacitySource` says so, because a missing row
 * means "not planned yet", not "no capacity".
 */
export const DeveloperWorkload = z.object({
    userId: z.string(),
    name: z.string(),
    email: z.string(),
    role: z.string(),
    active: z.boolean(),
    avatarUrl: z.string().nullable().default(null),
    // ---- work in flight ----
    inProgressCount: z.number().int(),
    openCount: z.number().int(),
    blockedCount: z.number().int(),
    /** Open tickets whose due date has passed. */
    overdueCount: z.number().int(),
    // ---- hours ----
    /** Sprint capacity hours for this developer, net of PTO. */
    capacityHours: z.number().nullable(),
    /** Hours already committed from assigned sprint tickets. */
    assignedHours: z.number(),
    /** capacityHours - assignedHours, floored at 0. Null when capacity is unknown. */
    availableHours: z.number().nullable(),
    utilizationPercent: z.number().nullable(),
    capacitySource: z.enum(['sprint_capacity', 'sprint_default', 'none']),
    /**
     * Why the capacity figure is what it is, when that needs explaining — an ended
     * sprint, a missing capacity row, an explicit zero. Null when planned capacity
     * was read straight from a SprintCapacity row and speaks for itself.
     *
     * This exists because "0h free" and "we have no idea" look identical in a
     * number but mean opposite things to whoever is staffing the queue.
     */
    capacityNote: z.string().nullable().default(null),
    // ---- sprint ----
    sprintId: z.string().nullable(),
    sprintName: z.string().nullable(),
    /** Whole days left in the active sprint, PTO removed. */
    sprintDaysRemaining: z.number().nullable(),
    ptoDays: z.number(),
    // ---- history ----
    /** Tickets resolved in the trailing history window. */
    resolvedRecent: z.number().int(),
    /** Median hours from first assignment to resolution, over the window. */
    medianResolutionHours: z.number().nullable(),
    /** Share of recent sprint commitments actually completed, 0..1. */
    completionRate: z.number().nullable(),
    /** Set when this developer cannot take work at all right now. */
    gates: z.array(AssignmentGate).default([]),
});
// ---------------------------------------------------------------------------
// Suggestions
// ---------------------------------------------------------------------------
export const AssignmentCandidate = z.object({
    userId: z.string(),
    name: z.string(),
    email: z.string(),
    avatarUrl: z.string().nullable().default(null),
    rank: z.number().int(),
    /** 0..100, the weighted sum of the factors below. */
    score: z.number(),
    /**
     * 0..100 — how separated this candidate is from the field, and how much
     * evidence backs the affinity signal. Low confidence means "thin history".
     */
    confidence: z.number(),
    factors: z.array(AssignmentFactor),
    /** One-line plain-English summary of why this person. */
    rationale: z.string(),
    /** Things the scorer wants a human to weigh — e.g. "no history in this area". */
    cautions: z.array(z.string()).default([]),
    workload: DeveloperWorkload,
});
export const TicketAssignmentSuggestion = z.object({
    ticketId: z.string(),
    ticketCode: z.string(),
    title: z.string(),
    type: TicketType,
    priority: Priority,
    severity: Severity.nullable(),
    productArea: z.string().nullable(),
    component: z.string().nullable(),
    repo: z.string().nullable(),
    /** Hours used for the capacity math: estimateHours, else aiEstimateHours, else null. */
    estimateHours: z.number().nullable(),
    estimateSource: z.enum(['human', 'ai', 'points', 'none']),
    /** Ranked best-first. Empty when every developer is gated out. */
    candidates: z.array(AssignmentCandidate),
    /** Persisted suggestion-run id, echoed back on apply so the outcome is recorded. */
    runId: z.string(),
    /** Set when no candidate could be produced, explaining why. */
    note: z.string().nullable().default(null),
});
export const AssignmentSuggestInput = z.object({
    /** Explicit ticket ids (or codes). Mutually exclusive with `aql`. */
    ticketIds: z.array(z.string()).max(100).optional(),
    /** Or: pull the queue from a filter. `assignee = unassigned` is always ANDed in. */
    aql: z.string().optional(),
    /** Candidates returned per ticket. */
    limit: z.number().int().min(1).max(10).default(3),
    /** Restrict candidates to these users (e.g. one squad). */
    userIds: z.array(z.string()).optional(),
    /** Ask the configured AI provider to write the rationale. Falls back silently. */
    explain: z.boolean().default(true),
});
export const ApplyAssignmentInput = z.object({
    ticketId: z.string(),
    /** Who to actually assign. May differ from the top candidate — that is an override. */
    userId: z.string(),
    /** The run this decision answers, so acceptance/override is attributable. */
    runId: z.string().optional(),
    /** Free text; the UI asks for it when the pick is not the top candidate. */
    overrideReason: z.string().max(500).optional(),
    /** Replace existing assignees rather than adding to them. */
    replace: z.boolean().default(true),
});
// ---------------------------------------------------------------------------
// Queue summary
// ---------------------------------------------------------------------------
/** Headline numbers for the unassigned queue — the "how many are unassigned" ask. */
export const UnassignedSummary = z.object({
    total: z.number().int(),
    /** Subset that is genuinely actionable (not draft/done/cancelled/rejected). */
    actionable: z.number().int(),
    byPriority: z.record(z.number().int()),
    byType: z.record(z.number().int()),
    byStatus: z.record(z.number().int()),
    /** Unassigned tickets sitting in the active sprint — the urgent slice. */
    inActiveSprint: z.number().int(),
    /** Unassigned and already past due. */
    overdue: z.number().int(),
    /** Age of the oldest unassigned ticket, in whole days. */
    oldestAgeDays: z.number().int().nullable(),
    /** Sum of estimate hours across the actionable queue (AI estimate as fallback). */
    totalEstimateHours: z.number(),
    /**
     * Free hours across all eligible developers in the active sprint.
     *
     * NULL when no eligible developer has a known capacity figure — an ended
     * sprint, or nobody planned. Summing unknowns to 0 would report the team as
     * having no hours, which reads as a crisis rather than as missing data.
     */
    teamAvailableHours: z.number().nullable(),
});
/** Acceptance tracking — how often humans take the agent's top pick. */
export const AssignmentAccuracy = z.object({
    windowDays: z.number().int(),
    decided: z.number().int(),
    accepted: z.number().int(),
    overridden: z.number().int(),
    /** accepted / decided, 0..1. Null until there is at least one decision. */
    acceptanceRate: z.number().nullable(),
    /** Users most often chosen INSTEAD of the top pick, with counts. */
    topOverrides: z.array(z.object({ userId: z.string(), name: z.string(), count: z.number().int() })),
});
// ---------------------------------------------------------------------------
// Reverse direction: find work for a person
// ---------------------------------------------------------------------------
/**
 * The other way round. "Devin is free — what should he pick up?"
 *
 * Same scoring engine, read along the other axis: instead of ranking developers
 * against one ticket, it ranks unassigned tickets against one developer.
 *
 * One deliberate difference. In the ticket-first direction a developer with no
 * bandwidth is GATED OUT — suggesting them would be wrong. Here the human has
 * already named the person, so refusing to answer is useless; their gates are
 * reported alongside the list instead, and the capacity factor still pushes work
 * that does not fit down the ranking.
 */
export const TicketFitSuggestion = z.object({
    ticketId: z.string(),
    ticketCode: z.string(),
    title: z.string(),
    type: TicketType,
    status: z.string(),
    priority: Priority,
    severity: Severity.nullable(),
    productArea: z.string().nullable(),
    component: z.string().nullable(),
    repo: z.string().nullable(),
    blocked: z.boolean(),
    dueDate: z.string().nullable(),
    createdAt: z.string(),
    estimateHours: z.number().nullable(),
    estimateSource: z.enum(['human', 'ai', 'points', 'none']),
    rank: z.number().int(),
    /** 0..100 — how well this person suits this ticket (the seven factors). */
    fitScore: z.number(),
    /**
     * 0..100 — how badly the BUSINESS wants this done (priority, severity,
     * overdue, sprint commitment, age). Deliberately separate from fit: a perfect
     * match on trivial work should not outrank a good match on a P1, and mixing
     * the two into one number would hide which is which.
     */
    urgencyScore: z.number(),
    /** The number the list is ordered by, per the caller's `sort`. */
    rankScore: z.number(),
    confidence: z.number(),
    factors: z.array(AssignmentFactor),
    rationale: z.string(),
    cautions: z.array(z.string()).default([]),
});
export const DeveloperWorkSuggestion = z.object({
    developer: DeveloperWorkload,
    items: z.array(TicketFitSuggestion),
    runId: z.string(),
    /** How the list was ordered. */
    sort: z.enum(['balanced', 'fit', 'urgency']),
    /** Total unassigned tickets considered before the limit was applied. */
    considered: z.number().int(),
    /** Set when the person cannot really take work — they are shown anyway. */
    note: z.string().nullable().default(null),
});
export const WorkForDeveloperInput = z.object({
    userId: z.string(),
    /** Narrow the pool. `assignee = unassigned` is always ANDed in. */
    aql: z.string().optional(),
    limit: z.number().int().min(1).max(50).default(10),
    /**
     * balanced (default) weighs fit 70 / urgency 30 — the ordering a lead actually
     * wants; `fit` and `urgency` sort purely by one axis for when they don't.
     */
    sort: z.enum(['balanced', 'fit', 'urgency']).default('balanced'),
    explain: z.boolean().default(true),
});
/** Weights for the `balanced` ordering. */
export const BALANCED_FIT_WEIGHT = 0.7;
export const BALANCED_URGENCY_WEIGHT = 0.3;
//# sourceMappingURL=assignment.js.map